import { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../../api';
import Modal from '../../components/Modal';

function fmt(value, mt) {
  if (value === null || value === undefined) return '';
  switch (mt) {
    case 'count': return Math.round(value).toLocaleString();
    case 'percentage': return `${value.toFixed(1)}%`;
    case 'currency': return `$${value.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
    case 'duration': return `${Math.round(value)}m`;
    case 'score': return value.toFixed(1);
    default: return String(value);
  }
}

function ini(name) {
  if (!name) return '?';
  return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
}

export default function DataEntryPage() {
  const [kpis, setKpis] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedKpi, setSelectedKpi] = useState(null);
  const [rounds, setRounds] = useState([]);
  const [activeRound, setActiveRound] = useState(null);
  const [members, setMembers] = useState([]);
  const [entries, setEntries] = useState({}); // { [userId]: value }
  const [cumulative, setCumulative] = useState({}); // { [userId]: total across all rounds }
  const [prefilledFrom, setPrefilledFrom] = useState({}); // { [userId]: { source round label } }
  const [saving, setSaving] = useState({});   // { [userId]: true/false }
  const [saved, setSaved] = useState({});     // { [userId]: true } flash
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Round management
  const [newRoundLabel, setNewRoundLabel] = useState('');
  const [addingRound, setAddingRound] = useState(false);
  const [editingRound, setEditingRound] = useState(null);
  const [editRoundLabel, setEditRoundLabel] = useState('');
  const [editRoundSubtitle, setEditRoundSubtitle] = useState('');
  const [resetConfirm, setResetConfirm] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [deleteRoundConfirm, setDeleteRoundConfirm] = useState(null);

  // CSV
  const [showCsv, setShowCsv] = useState(false);
  const [csvText, setCsvText] = useState('');
  const [uploading, setUploading] = useState(false);

  const saveTimers = useRef({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.getLeaderboardKpis();
      setKpis(Array.isArray(data) ? data.filter(k => k.input_method === 'manual' || k.input_method === 'csv') : []);
    } catch { setKpis([]); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // Load entries for a round given a member list.
  async function loadEntries(kpiId, round, memberList) {
    setActiveRound(round);
    setEntries({});
    setPrefilledFrom({});
    try {
      const data = await api.getLeaderboardEntries({ kpi_id: kpiId, round_id: round.id });
      const map = {};
      if (Array.isArray(data)) {
        for (const e of data) map[e.user_id] = e.value;
      }
      const full = {};
      for (const m of memberList) {
        full[m.id] = map[m.id] !== undefined && map[m.id] !== null ? String(map[m.id]) : '';
      }
      setEntries(full);
    } catch (err) {
      console.error('[DataEntry] loadEntries failed:', err);
      setError('Failed to load scores');
    }
    loadCumulative(kpiId);
  }

  // Pull every entry for this KPI across all rounds and sum per user
  async function loadCumulative(kpiId) {
    try {
      const data = await api.getLeaderboardEntries({ kpi_id: kpiId });
      const totals = {};
      if (Array.isArray(data)) {
        for (const e of data) {
          totals[e.user_id] = (totals[e.user_id] || 0) + (Number(e.value) || 0);
        }
      }
      setCumulative(totals);
    } catch { /* swallow */ }
  }

  // When KPI changes, load rounds + members, auto-select the round
  // that has the most recent data (so admins land on the round they
  // last updated, not an empty new auto-round).
  async function selectKpi(kpi) {
    setSelectedKpi(kpi);
    setActiveRound(null);
    setEntries({});
    setError('');
    try {
      const [roundList, memberList, allEntries] = await Promise.all([
        api.getLeaderboardRounds(kpi.id),
        api.getLeaderboardKpiMembers(kpi.id),
        api.getLeaderboardEntries({ kpi_id: kpi.id }),
      ]);
      const rl = Array.isArray(roundList) ? roundList : [];
      const ml = Array.isArray(memberList) ? memberList : [];
      const allEntriesList = Array.isArray(allEntries) ? allEntries : [];

      setRounds(rl);
      setMembers(ml);

      if (rl.length > 0 && ml.length > 0) {
        // Walk rounds newest → oldest, find the first round that has any
        // entries. If no round has entries, fall back to the newest round.
        let selectedRound = rl[rl.length - 1];
        for (let i = rl.length - 1; i >= 0; i--) {
          if (allEntriesList.some(e => e.round_id === rl[i].id)) {
            selectedRound = rl[i];
            break;
          }
        }
        await loadEntries(kpi.id, selectedRound, ml);
      }
    } catch (err) {
      console.error('[DataEntry] selectKpi failed:', err);
      setRounds([]);
      setMembers([]);
    }
  }

  // When round is clicked, load entries for that round
  function selectRound(round) {
    loadEntries(selectedKpi.id, round, members);
  }

  // Auto-save on value change (debounced)
  function handleValueChange(userId, val) {
    setEntries(prev => ({ ...prev, [userId]: val }));
    // Typing clears the "pre-filled from another round" indicator —
    // once they touch it, it's their value for THIS round
    setPrefilledFrom(prev => {
      if (!prev[userId]) return prev;
      const next = { ...prev };
      delete next[userId];
      return next;
    });

    // Clear existing timer
    if (saveTimers.current[userId]) clearTimeout(saveTimers.current[userId]);

    // Don't save empty values
    if (val === '' || isNaN(parseFloat(val))) return;

    // Debounce save
    saveTimers.current[userId] = setTimeout(async () => {
      setSaving(prev => ({ ...prev, [userId]: true }));
      try {
        await api.createLeaderboardEntry({
          kpi_id: selectedKpi.id,
          round_id: activeRound.id,
          user_id: userId,
          value: parseFloat(val),
        });
        setSaved(prev => ({ ...prev, [userId]: true }));
        setTimeout(() => setSaved(prev => ({ ...prev, [userId]: false })), 1500);
        // Refresh cumulative so the "leaderboard total" stays in sync
        loadCumulative(selectedKpi.id);
      } catch (err) {
        setError(`Failed to save for user: ${err.message}`);
        setTimeout(() => setError(''), 4000);
      }
      setSaving(prev => ({ ...prev, [userId]: false }));
    }, 600);
  }

  // Save on Enter key
  function handleKeyDown(e, userId) {
    if (e.key === 'Enter') {
      e.preventDefault();
      const val = entries[userId];
      if (val === '' || isNaN(parseFloat(val))) return;
      if (saveTimers.current[userId]) clearTimeout(saveTimers.current[userId]);
      (async () => {
        setSaving(prev => ({ ...prev, [userId]: true }));
        try {
          await api.createLeaderboardEntry({
            kpi_id: selectedKpi.id,
            round_id: activeRound.id,
            user_id: userId,
            value: parseFloat(val),
          });
          setSaved(prev => ({ ...prev, [userId]: true }));
          setTimeout(() => setSaved(prev => ({ ...prev, [userId]: false })), 1500);
        } catch (err) {
          setError(`Failed to save: ${err.message}`);
          setTimeout(() => setError(''), 4000);
        }
        setSaving(prev => ({ ...prev, [userId]: false }));
      })();
      // Focus next input
      const inputs = document.querySelectorAll('.scorecard-input');
      const idx = Array.from(inputs).findIndex(el => el === e.target);
      if (idx >= 0 && idx < inputs.length - 1) inputs[idx + 1].focus();
    }
  }

  // Add round
  async function handleAddRound(e) {
    e.preventDefault();
    if (!newRoundLabel.trim()) return;
    setAddingRound(true);
    setError('');
    try {
      const round = await api.createLeaderboardRound({ kpi_id: selectedKpi.id, label: newRoundLabel.trim() });
      setRounds(prev => [...prev, round]);
      setNewRoundLabel('');
      selectRound(round);
    } catch (err) { setError(err.message); }
    setAddingRound(false);
  }

  // Edit round label + subtitle
  async function handleEditRound(e) {
    e.preventDefault();
    if (!editRoundLabel.trim()) return;
    const newLabel = editRoundLabel.trim();
    const newSubtitle = editRoundSubtitle.trim();
    try {
      await api.updateLeaderboardRound(editingRound.id, { label: newLabel, subtitle: newSubtitle });
      setRounds(prev => prev.map(r => r.id === editingRound.id ? { ...r, label: newLabel, subtitle: newSubtitle } : r));
      if (activeRound?.id === editingRound.id) setActiveRound(prev => ({ ...prev, label: newLabel, subtitle: newSubtitle }));
      setEditingRound(null);
      setSuccess('Round updated');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) { setError(err.message); }
  }

  // Reset current pay period — clears all entries for this KPI but keeps
  // the rounds. Used at the start of each new pay period so Week 1 and
  // Week 2 inputs go back to blank without deleting the rounds.
  async function handleResetKpi() {
    if (!selectedKpi) return;
    setResetting(true);
    try {
      await api.resetKpiEntries(selectedKpi.id);
      setResetConfirm(false);
      setSuccess('Pay period reset — all entries cleared for this KPI');
      setTimeout(() => setSuccess(''), 4000);
      // Reload entries for the active round (they'll all be blank now)
      if (activeRound) await loadEntries(selectedKpi.id, activeRound, members);
    } catch (err) {
      setError(err.message || 'Reset failed');
    }
    setResetting(false);
  }

  // Delete round
  async function handleDeleteRound(round) {
    try {
      await api.deleteLeaderboardRound(round.id);
      setRounds(prev => prev.filter(r => r.id !== round.id));
      if (activeRound?.id === round.id) { setActiveRound(null); setEntries({}); }
      setDeleteRoundConfirm(null);
      setSuccess('Round deleted');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) { setError(err.message); setDeleteRoundConfirm(null); }
  }

  // CSV upload
  async function handleCsvUpload(e) {
    e.preventDefault();
    if (!csvText.trim()) return;
    setUploading(true);
    setError('');
    try {
      const result = await api.bulkUploadLeaderboardEntries({ kpi_id: selectedKpi.id, round_id: activeRound.id, data: csvText });
      setSuccess(`${result.created} entries saved, ${result.skipped} skipped`);
      setCsvText('');
      setShowCsv(false);
      selectRound(activeRound); // refresh
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) { setError(err.message); }
    setUploading(false);
  }

  // Compute team total from current entries
  const teamTotal = Object.values(entries).reduce((sum, v) => {
    const n = parseFloat(v);
    return sum + (isNaN(n) ? 0 : n);
  }, 0);

  const medalColors = ['#6d28d9', '#7c3aed', '#a78bfa'];

  if (loading) return <div className="admin-page"><div className="loading-spinner">Loading...</div></div>;

  return (
    <div className="admin-page">
      <div className="maint-types__header">
        <h1>Data Entry</h1>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      {/* ─── KPI SELECTOR ─── */}
      {!selectedKpi ? (
        <div>
          <p style={{ color: '#64748b', fontSize: 13, marginBottom: 16 }}>Select a KPI to enter data.</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
            {kpis.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>No KPIs configured. Create one in KPI Setup.</div>
            ) : kpis.map(kpi => (
              <button key={kpi.id} onClick={() => selectKpi(kpi)} style={{
                background: '#fff', borderRadius: 12, padding: '20px', border: '1px solid #e2e8f0',
                cursor: 'pointer', textAlign: 'left', transition: 'all 0.15s',
                boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
              }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = '#6d28d9'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(109,40,217,0.12)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.04)'; }}
              >
                <div style={{ fontSize: 15, fontWeight: 700, color: '#0f172a', marginBottom: 4 }}>{kpi.name}</div>
                {kpi.description && <div style={{ fontSize: 12, color: '#64748b', marginBottom: 8 }}>{kpi.description}</div>}
                <div style={{ display: 'flex', gap: 6 }}>
                  <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 4, background: '#6d28d915', color: '#6d28d9' }}>
                    {kpi.round_mode === 'auto' ? 'auto' : 'manual'} rounds
                  </span>
                  <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 4, background: '#f1f5f9', color: '#64748b' }}>
                    {kpi.metric_type}
                  </span>
                </div>
              </button>
            ))}
          </div>
        </div>
      ) : (
        <>
          {/* ─── BREADCRUMB / BACK ─── */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
            <button onClick={() => { setSelectedKpi(null); setActiveRound(null); setRounds([]); setMembers([]); setEntries({}); }} style={{
              background: '#f1f5f9', border: 'none', borderRadius: 8, padding: '8px 14px',
              cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#64748b', display: 'flex', alignItems: 'center', gap: 6,
            }}>
              &#x2190; All KPIs
            </button>
            <div>
              <span style={{ fontSize: 18, fontWeight: 800, color: '#0f172a' }}>{selectedKpi.name}</span>
              <span style={{ fontSize: 12, color: '#94a3b8', marginLeft: 10 }}>{selectedKpi.metric_type} &middot; {selectedKpi.value_direction === 'higher_is_better' ? '↑ Higher is better' : '↓ Lower is better'}</span>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '260px 1fr', gap: 24, alignItems: 'start' }}>

            {/* ─── LEFT: ROUNDS LIST ─── */}
            <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
              <div style={{ padding: '16px 18px 12px', borderBottom: '1px solid #f1f5f9' }}>
                <h3 style={{ margin: 0, fontSize: 13, fontWeight: 800, color: '#475569', textTransform: 'uppercase', letterSpacing: 0.5 }}>Rounds</h3>
              </div>

              {rounds.length === 0 ? (
                <div style={{ padding: '24px 18px', textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
                  No rounds yet. Add one below.
                </div>
              ) : (
                <div style={{ maxHeight: 400, overflowY: 'auto' }}>
                  {rounds.map((round, idx) => {
                    const isActive = activeRound?.id === round.id;
                    return (
                      <div key={round.id} style={{
                        padding: '14px 18px', cursor: 'pointer', transition: 'all 0.12s',
                        background: isActive ? 'linear-gradient(90deg, #f5f3ff, #ede9fe)' : 'transparent',
                        borderLeft: isActive ? '3px solid #6d28d9' : '3px solid transparent',
                        borderBottom: idx < rounds.length - 1 ? '1px solid #f8fafc' : 'none',
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
                      }}
                        onClick={() => selectRound(round)}
                      >
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{
                            fontSize: 14, fontWeight: isActive ? 700 : 600,
                            color: isActive ? '#6d28d9' : '#0f172a',
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                          }}>
                            {round.label}
                          </div>
                          {round.subtitle && (
                            <div style={{
                              fontSize: 11, fontWeight: 500,
                              color: isActive ? '#8b5cf6' : '#94a3b8',
                              marginTop: 2,
                              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                            }}>
                              {round.subtitle}
                            </div>
                          )}
                        </div>
                        {/* Edit / Delete icons */}
                        <div style={{ display: 'flex', gap: 4, flexShrink: 0 }} onClick={e => e.stopPropagation()}>
                          <button onClick={() => { setEditingRound(round); setEditRoundLabel(round.label); setEditRoundSubtitle(round.subtitle || ''); }} style={{
                            background: 'none', border: 'none', cursor: 'pointer', padding: '4px 6px', borderRadius: 4,
                            fontSize: 12, color: '#94a3b8', transition: 'color 0.1s',
                          }}
                            onMouseEnter={e => e.currentTarget.style.color = '#6d28d9'}
                            onMouseLeave={e => e.currentTarget.style.color = '#94a3b8'}
                            title="Rename round"
                          >
                            &#x270E;
                          </button>
                          <button onClick={() => setDeleteRoundConfirm(round)} style={{
                            background: 'none', border: 'none', cursor: 'pointer', padding: '4px 6px', borderRadius: 4,
                            fontSize: 12, color: '#94a3b8', transition: 'color 0.1s',
                          }}
                            onMouseEnter={e => e.currentTarget.style.color = '#ef4444'}
                            onMouseLeave={e => e.currentTarget.style.color = '#94a3b8'}
                            title="Delete round"
                          >
                            &#x1F5D1;
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Add round */}
              {selectedKpi.round_mode !== 'auto' && (
                <form onSubmit={handleAddRound} style={{
                  padding: '12px 14px', borderTop: '1px solid #f1f5f9',
                  display: 'flex', gap: 8, alignItems: 'center', background: '#fafbfc',
                }}>
                  <input
                    value={newRoundLabel}
                    onChange={e => setNewRoundLabel(e.target.value)}
                    placeholder="New round name..."
                    style={{
                      flex: 1, padding: '8px 12px', borderRadius: 8, border: '1px solid #e2e8f0',
                      fontSize: 13, outline: 'none', background: '#fff',
                    }}
                    onFocus={e => e.target.style.borderColor = '#6d28d9'}
                    onBlur={e => e.target.style.borderColor = '#e2e8f0'}
                  />
                  <button type="submit" disabled={addingRound || !newRoundLabel.trim()} style={{
                    background: '#6d28d9', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 14px',
                    fontSize: 12, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap',
                    opacity: addingRound || !newRoundLabel.trim() ? 0.5 : 1,
                  }}>
                    + Add
                  </button>
                </form>
              )}
            </div>

            {/* ─── RIGHT: SCORECARD ─── */}
            <div>
              {!activeRound ? (
                <div style={{
                  background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0', padding: '60px 20px',
                  textAlign: 'center', color: '#94a3b8',
                }}>
                  <div style={{ fontSize: 42, marginBottom: 12, opacity: 0.5 }}>&#x1F4CB;</div>
                  <p style={{ fontSize: 15, fontWeight: 600, color: '#64748b' }}>Select a round to edit scores</p>
                  <p style={{ fontSize: 13 }}>Click a round on the left to open the scorecard.</p>
                </div>
              ) : (
                <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                  {/* Scorecard header */}
                  <div style={{
                    padding: '18px 24px', borderBottom: '2px solid #e2e8f0',
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    background: 'linear-gradient(135deg, #f5f3ff, #ede9fe)',
                  }}>
                    <div>
                      <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: '#6d28d9' }}>{activeRound.label}</h2>
                      {activeRound.subtitle && (
                        <div style={{ fontSize: 13, color: '#8b5cf6', fontWeight: 600, marginTop: 2 }}>
                          {activeRound.subtitle}
                        </div>
                      )}
                      <div style={{ fontSize: 12, color: '#7c3aed', fontWeight: 600, marginTop: 2 }}>{members.length} member{members.length !== 1 ? 's' : ''}</div>
                    </div>
                    <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                      {/* Team total */}
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.5 }}>Team Total</div>
                        <div style={{ fontSize: 22, fontWeight: 900, color: '#6d28d9', fontVariantNumeric: 'tabular-nums' }}>
                          {fmt(teamTotal, selectedKpi.metric_type)}
                        </div>
                      </div>
                      {/* Reset Pay Period button */}
                      <button
                        onClick={() => setResetConfirm(true)}
                        title="Clear all entries for this KPI (keeps rounds)"
                        style={{
                          background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca',
                          borderRadius: 8, padding: '8px 14px', cursor: 'pointer',
                          fontSize: 11, fontWeight: 700, transition: 'all 0.15s',
                        }}
                        onMouseEnter={e => { e.currentTarget.style.background = '#b91c1c'; e.currentTarget.style.color = '#fff'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = '#fef2f2'; e.currentTarget.style.color = '#b91c1c'; }}
                      >
                        ⟲ Reset Pay Period
                      </button>
                      {/* CSV toggle */}
                      <button onClick={() => setShowCsv(!showCsv)} style={{
                        background: showCsv ? '#6d28d9' : '#f1f5f9', color: showCsv ? '#fff' : '#64748b',
                        border: 'none', borderRadius: 8, padding: '8px 14px', cursor: 'pointer',
                        fontSize: 11, fontWeight: 700, transition: 'all 0.15s',
                      }}>
                        CSV
                      </button>
                    </div>
                  </div>

                  {/* CSV upload area (collapsible) */}
                  {showCsv && (
                    <div style={{ padding: '16px 24px', borderBottom: '1px solid #e2e8f0', background: '#fafbfc' }}>
                      <form onSubmit={handleCsvUpload}>
                        <div style={{ fontSize: 11, color: '#64748b', marginBottom: 8 }}>
                          <strong>Usernames:</strong> {members.map(m => m.username).join(', ')}
                        </div>
                        <textarea
                          value={csvText}
                          onChange={e => setCsvText(e.target.value)}
                          placeholder={"username,value\njsmith,47\njdoe,32"}
                          rows={4}
                          style={{
                            width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #e2e8f0',
                            fontFamily: 'monospace', fontSize: 12, resize: 'vertical', outline: 'none', boxSizing: 'border-box',
                          }}
                        />
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
                          <button type="button" onClick={() => setShowCsv(false)} style={{
                            background: '#f1f5f9', border: 'none', borderRadius: 6, padding: '7px 14px',
                            fontSize: 12, fontWeight: 600, color: '#64748b', cursor: 'pointer',
                          }}>Cancel</button>
                          <button type="submit" disabled={uploading || !csvText.trim()} style={{
                            background: '#6d28d9', color: '#fff', border: 'none', borderRadius: 6, padding: '7px 14px',
                            fontSize: 12, fontWeight: 700, cursor: 'pointer', opacity: uploading || !csvText.trim() ? 0.5 : 1,
                          }}>
                            {uploading ? 'Uploading...' : 'Upload'}
                          </button>
                        </div>
                      </form>
                    </div>
                  )}

                  {/* Scorecard rows */}
                  {members.length === 0 ? (
                    <div style={{ padding: '40px 20px', textAlign: 'center', color: '#94a3b8' }}>
                      <p style={{ fontSize: 14, fontWeight: 600, color: '#64748b' }}>No members assigned to this KPI's group.</p>
                      <a href="/admin-mod/leaderboard-groups" style={{ fontSize: 13, color: '#6d28d9', fontWeight: 600 }}>Manage Groups →</a>
                    </div>
                  ) : (
                    <div>
                      {/* Column headers */}
                      <div style={{
                        display: 'grid', gridTemplateColumns: '1fr 140px 50px',
                        padding: '10px 24px', borderBottom: '1px solid #f1f5f9',
                        fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.5,
                      }}>
                        <span>Name</span>
                        <span style={{ textAlign: 'center' }}>Value</span>
                        <span style={{ textAlign: 'center' }}></span>
                      </div>

                      {members.map((member, idx) => {
                        const val = entries[member.id] ?? '';
                        const isSaving = saving[member.id];
                        const isSaved = saved[member.id];
                        const hasValue = val !== '' && !isNaN(parseFloat(val));
                        const rank = idx + 1; // visual only
                        const rc = rank <= 3 ? medalColors[rank - 1] : '#94a3b8';

                        return (
                          <div key={member.id} style={{
                            display: 'grid', gridTemplateColumns: '1fr 140px 50px', alignItems: 'center',
                            padding: '12px 24px', borderBottom: idx < members.length - 1 ? '1px solid #f8fafc' : 'none',
                            transition: 'background 0.15s',
                            background: isSaved ? '#f0fdf4' : 'transparent',
                          }}>
                            {/* Name */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                              {member.photo_url ? (
                                <img src={member.photo_url} alt={member.full_name} style={{
                                  width: 36, height: 36, borderRadius: '50%', flexShrink: 0, objectFit: 'cover',
                                  border: '2px solid #e2e8f0',
                                }} />
                              ) : (
                                <div style={{
                                  width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
                                  background: '#f1f5f9', border: '2px solid #e2e8f0',
                                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  fontSize: 13, fontWeight: 700, color: '#64748b',
                                }}>
                                  {ini(member.full_name)}
                                </div>
                              )}
                              <div>
                                <div style={{ fontSize: 14, fontWeight: 600, color: '#0f172a' }}>{member.full_name}</div>
                                <div style={{ fontSize: 11, color: '#94a3b8' }}>
                                  {member.role}
                                  {cumulative[member.id] !== undefined && (
                                    <>
                                      <span style={{ margin: '0 6px', color: '#cbd5e1' }}>·</span>
                                      <span style={{ color: '#6d28d9', fontWeight: 700 }}>
                                        Leaderboard total: {fmt(cumulative[member.id], selectedKpi?.metric_type)}
                                      </span>
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>

                            {/* Value input */}
                            <div style={{ position: 'relative' }}>
                              <input
                                className="scorecard-input"
                                type="number"
                                step="any"
                                value={val}
                                onChange={e => handleValueChange(member.id, e.target.value)}
                                onKeyDown={e => handleKeyDown(e, member.id)}
                                placeholder="—"
                                style={{
                                  width: '100%', padding: '10px 14px', borderRadius: 10,
                                  border: isSaved ? '2px solid #22c55e' : '1px solid #e2e8f0',
                                  fontSize: 16, fontWeight: 700, textAlign: 'center',
                                  outline: 'none', transition: 'all 0.2s', boxSizing: 'border-box',
                                  background: hasValue ? '#faf8ff' : '#fff',
                                  color: '#0f172a',
                                }}
                                onFocus={e => { e.target.style.borderColor = '#6d28d9'; e.target.style.boxShadow = '0 0 0 3px rgba(109,40,217,0.1)'; }}
                                onBlur={e => { e.target.style.borderColor = isSaved ? '#22c55e' : '#e2e8f0'; e.target.style.boxShadow = 'none'; }}
                              />
                            </div>

                            {/* Status */}
                            <div style={{ textAlign: 'center', fontSize: 14 }}>
                              {isSaving ? (
                                <span style={{ color: '#94a3b8', fontSize: 12 }}>...</span>
                              ) : isSaved ? (
                                <span style={{ color: '#22c55e', fontSize: 16 }}>&#x2713;</span>
                              ) : hasValue ? (
                                <span style={{ color: '#cbd5e1', fontSize: 14 }}>&#x2713;</span>
                              ) : null}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Footer with team total */}
                  {members.length > 0 && (
                    <div style={{
                      padding: '14px 24px', borderTop: '2px solid #e2e8f0',
                      display: 'grid', gridTemplateColumns: '1fr 140px 50px', alignItems: 'center',
                      background: 'linear-gradient(135deg, #f5f3ff, #ede9fe)',
                    }}>
                      <span style={{ fontSize: 13, fontWeight: 800, color: '#6d28d9', textTransform: 'uppercase', letterSpacing: 0.5 }}>Team Total</span>
                      <div style={{ fontSize: 20, fontWeight: 900, color: '#6d28d9', textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}>
                        {fmt(teamTotal, selectedKpi.metric_type)}
                      </div>
                      <div />
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* ─── EDIT ROUND MODAL ─── */}
      {editingRound && (
        <Modal title="Edit Round" onClose={() => setEditingRound(null)}>
          <form onSubmit={handleEditRound}>
            <div className="form-group">
              <label>Label</label>
              <input
                value={editRoundLabel}
                onChange={e => setEditRoundLabel(e.target.value)}
                className="form-input"
                autoFocus
                placeholder="Week 1"
              />
              <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>
                Short name shown as the round header (e.g. "Week 1", "Round 3")
              </div>
            </div>
            <div className="form-group">
              <label>Subtitle (optional)</label>
              <input
                value={editRoundSubtitle}
                onChange={e => setEditRoundSubtitle(e.target.value)}
                className="form-input"
                placeholder="Apr 6 - Apr 12"
              />
              <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>
                Extra line shown under the label — useful for date ranges
              </div>
            </div>
            <div className="modal-actions">
              <button type="button" className="btn btn-outline" onClick={() => setEditingRound(null)}>Cancel</button>
              <button type="submit" className="btn btn-gold" disabled={!editRoundLabel.trim()}>Save</button>
            </div>
          </form>
        </Modal>
      )}

      {/* ─── RESET KPI CONFIRM MODAL ─── */}
      {resetConfirm && (
        <Modal title="Reset Pay Period?" onClose={() => setResetConfirm(false)}>
          <p style={{ margin: '0 0 14px', fontSize: 14, color: '#475569', lineHeight: 1.5 }}>
            This will <b>clear all entries</b> for <b>{selectedKpi?.name}</b> across every round. Rounds themselves (Week 1, Week 2) stay in place — just the values inside them are wiped.
          </p>
          <p style={{ margin: '0 0 14px', fontSize: 13, color: '#dc2626', fontWeight: 700 }}>
            This cannot be undone.
          </p>
          <div className="modal-actions">
            <button type="button" className="btn btn-outline" onClick={() => setResetConfirm(false)} disabled={resetting}>Cancel</button>
            <button type="button" className="btn" onClick={handleResetKpi} disabled={resetting} style={{ background: '#dc2626', color: '#fff', border: 'none' }}>
              {resetting ? 'Resetting...' : 'Yes, Reset'}
            </button>
          </div>
        </Modal>
      )}

      {/* ─── DELETE ROUND CONFIRM ─── */}
      {deleteRoundConfirm && (
        <Modal title="Delete Round" onClose={() => setDeleteRoundConfirm(null)}>
          <p>Delete <strong>{deleteRoundConfirm.label}</strong>?</p>
          <p style={{ fontSize: 13, color: '#ef4444', fontWeight: 600 }}>All entries in this round will be permanently deleted.</p>
          <div className="modal-actions">
            <button className="btn btn-outline" onClick={() => setDeleteRoundConfirm(null)}>Cancel</button>
            <button className="btn btn-danger" onClick={() => handleDeleteRound(deleteRoundConfirm)}>Delete Round</button>
          </div>
        </Modal>
      )}
    </div>
  );
}
